/**
 * Fuel Leakage Detection Algorithm
 * 
 * Detects possible fuel leakage events from time-series telemetry data.
 * Works for vehicles and stationary equipment using fuel level and accelerometer data.
 * 
 * Algorithm handles:
 * - Filtered/smoothed fuel data (high filtration level)
 * - Apparent drops caused by tilt/slosh
 * - Equipment without movement data (excavators, generators)
 * - Recovery detection after reorientation/activity
 */

// Event status constants
export const EVENT_STATUS = {
  POSSIBLE_UNCONFIRMED: 'POSSIBLE_UNCONFIRMED',
  POSSIBLE_LEAKAGE_SENSOR_AFFECTED: 'POSSIBLE_LEAKAGE_SENSOR_AFFECTED',
  DISMISSED_TILT_SLOSH: 'DISMISSED_TILT_SLOSH',
  DISMISSED_SETTLING: 'DISMISSED_SETTLING',
  DISMISSED_SENSOR_ERROR: 'DISMISSED_SENSOR_ERROR',
  POSSIBLE_LEAKAGE: 'POSSIBLE_LEAKAGE',
  CONFIRMED_LEAKAGE: 'CONFIRMED_LEAKAGE'
};

// Default configuration
export const DEFAULT_CONFIG = {
  // Minimum meaningful loss (liters) - lowered from 10 to 5 since improved sloshing detection handles small oscillations
  D_min_liters: 5,
  // Pre-window for baseline calculation (minutes)
  pre_window_minutes: 10,
  // Post-window for baseline calculation (minutes)
  post_window_minutes: 15,
  // Minimum event separation (minutes) - merge nearby events
  min_event_separation_minutes: 30,
  // Tilt threshold (degrees)
  T_tilt_deg: 6,
  // Filter lag from device filtering (minutes)
  filter_lag_minutes: 3,
  // Recovery window after opportunity (minutes)
  recovery_window_minutes: 20,
  // Recovery ratio to dismiss as tilt/slosh
  recovery_ratio_dismiss: 0.7,
  // Recovery ratio threshold to confirm leakage
  recovery_ratio_confirm: 0.2,
  // Activity sustain duration (seconds)
  active_sustain_seconds: 120,
  // Vibration threshold strategy ('p95', 'p90', or fixed value)
  vibe_threshold_strategy: 'p95',
  // Maximum stddev for post-window stability (liters)
  sigma_max: 2,
  // Speed threshold for movement detection (km/h)
  speed_threshold_kmh: 10,
  // Distance threshold for movement (meters)
  distance_threshold_m: 500,
  // Smoothing parameters
  smoothing_window: 7, // Rolling median window
  ewma_alpha: 0.2, // EWMA alpha
  // Gravity estimation window (samples) - about 20-60 seconds at 10s sampling
  gravity_window: 6,
  // Tilt comparison delta (seconds)
  tilt_delta_seconds: 90,
  // Vibration calculation window (samples)
  vibe_window: 3,
  // Settling detection: minimum idle/parked duration before drop to consider settling (minutes)
  min_idle_duration_for_settling_minutes: 60,
  // Settling detection: window around drop to check for activity resumption (minutes)
  // Set generously to account for device filter lag (filtered fuel drops appear delayed)
  settling_activity_window_minutes: 15,
  // Pre-drop spike detection: how far back to look for a stable baseline before any spike (minutes)
  pre_spike_window_minutes: 30,
  // Pre-drop spike detection: minimum spike above stable baseline to flag (liters)
  pre_spike_threshold_liters: 5,
  // Pre-drop spike detection: how close post-level must be to stable baseline to confirm slosh (liters)
  post_vs_baseline_tolerance_liters: 5,
  // Pre-drop volatility: window to measure fuel stddev before the drop (minutes)
  pre_drop_volatility_window_minutes: 20,
  // Pre-drop volatility: stddev threshold (liters) above which fuel is considered "volatile"
  pre_drop_volatility_threshold: 3,
  // Operational state check: window to check equipment state (minutes)
  operational_check_window_minutes: 15,
  // Operational state: fraction of samples that must be "actively working" (ign=1, idle=0)
  working_fraction_threshold: 0.5,
  // Refuel proximity: window to check for nearby refuel events (minutes)
  refuel_proximity_minutes: 30,
  // Symmetry: max ratio of pre-spike to loss before spike is considered a refuel (not slosh)
  spike_symmetry_max_ratio: 2.0,
  // Symmetry: max ratio of recovery to loss before recovery is considered a refuel (not slosh)
  recovery_symmetry_max_ratio: 2.0,
  // Lag-aware state check window (minutes) ending at (t_detect - filter_lag)
  state_lag_window_minutes: 5,
  // Recent raw state check window right before t_detect (minutes)
  state_recent_window_minutes: 3,
  // Sensor error detection window around drop (minutes)
  sensor_error_window_minutes: 10,
  // Sensor error continuity threshold (minutes) to strongly dismiss as sensor artifact
  sensor_error_strong_duration_minutes: 3,
  // Minimum sensor-error packets for strong sensor artifact classification
  sensor_error_strong_min_samples: 8
};

/**
 * Compute rolling median of an array
 */
function rollingMedian(arr, windowSize) {
  const result = [];
  const halfWindow = Math.floor(windowSize / 2);
  
  for (let i = 0; i < arr.length; i++) {
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(arr.length, i + halfWindow + 1);
    const window = arr.slice(start, end).filter(v => v != null && !isNaN(v)).sort((a, b) => a - b);
    
    if (window.length === 0) {
      result.push(arr[i]);
    } else {
      const mid = Math.floor(window.length / 2);
      result.push(window.length % 2 === 0 ? (window[mid - 1] + window[mid]) / 2 : window[mid]);
    }
  }
  
  return result;
}

/**
 * Compute EWMA (Exponentially Weighted Moving Average)
 */
function ewma(arr, alpha) {
  const result = [];
  let prev = arr[0];
  
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] == null || isNaN(arr[i])) {
      result.push(prev);
    } else {
      prev = alpha * arr[i] + (1 - alpha) * (prev || arr[i]);
      result.push(prev);
    }
  }
  
  return result;
}

/**
 * Compute standard deviation of an array
 */
function stddev(arr) {
  const validArr = arr.filter(v => v != null && !isNaN(v));
  if (validArr.length === 0) return 0;
  
  const mean = validArr.reduce((a, b) => a + b, 0) / validArr.length;
  const squaredDiffs = validArr.map(v => Math.pow(v - mean, 2));
  return Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / validArr.length);
}

/**
 * Compute median of an array
 */
function median(arr) {
  const validArr = arr.filter(v => v != null && !isNaN(v)).sort((a, b) => a - b);
  if (validArr.length === 0) return 0;
  
  const mid = Math.floor(validArr.length / 2);
  return validArr.length % 2 === 0 ? (validArr[mid - 1] + validArr[mid]) / 2 : validArr[mid];
}

/**
 * Compute percentile of an array
 */
function percentile(arr, p) {
  const validArr = arr.filter(v => v != null && !isNaN(v)).sort((a, b) => a - b);
  if (validArr.length === 0) return 0;
  
  const index = (p / 100) * (validArr.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  
  if (lower === upper) return validArr[lower];
  return validArr[lower] * (upper - index) + validArr[upper] * (index - lower);
}

/**
 * Compute vector magnitude
 */
function magnitude(vec) {
  return Math.sqrt(vec.reduce((sum, v) => sum + v * v, 0));
}

/**
 * Normalize a vector
 */
function normalize(vec) {
  const mag = magnitude(vec);
  return mag === 0 ? vec : vec.map(v => v / mag);
}

/**
 * Dot product of two vectors
 */
function dot(a, b) {
  return a.reduce((sum, v, i) => sum + v * (b[i] || 0), 0);
}

/**
 * Convert timestamp to milliseconds epoch
 */
function toEpoch(timestamp) {
  if (typeof timestamp === 'number') {
    // If already in milliseconds (> year 2000 in ms)
    if (timestamp > 946684800000) return timestamp;
    // If in seconds
    return timestamp * 1000;
  }
  return new Date(timestamp).getTime();
}

/**
 * Preprocess raw telemetry data
 */
function preprocessData(samples, config) {
  // Sort by timestamp and dedupe
  const sorted = [...samples].sort((a, b) => {
    const tsA = toEpoch(a.timestamp || a.datetime_ist);
    const tsB = toEpoch(b.timestamp || b.datetime_ist);
    return tsA - tsB;
  });
  
  // Remove duplicates (same timestamp)
  const deduped = [];
  let lastTs = null;
  for (const sample of sorted) {
    const ts = toEpoch(sample.timestamp || sample.datetime_ist);
    if (ts !== lastTs) {
      deduped.push({ ...sample, _ts: ts });
      lastTs = ts;
    }
  }
  
  // Extract and validate required fields
  const processed = deduped.map(sample => ({
    timestamp: sample._ts,
    datetime_ist: sample.datetime_ist,
    fuelLevelLiters: parseFloat(sample.fuelLevelLiters) || 0,
    x_axis: parseFloat(sample.x_axis) || 0,
    y_axis: parseFloat(sample.y_axis) || 0,
    z_axis: parseFloat(sample.z_axis) || 0,
    speed_gnss: parseFloat(sample.speed_gnss) || 0,
    calculated_ignition: parseInt(sample.calculated_ignition) || parseInt(sample.ignition) || 0,
    calculated_idle: parseInt(sample.calculated_idle) || 0,
    movement: parseInt(sample.movement) || 0,
    odometer_kms: parseFloat(sample.odometer_kms) || 0,
    lat_long: sample.lat_long || null,
    flag_to_check: sample.flag_to_check || 'OK',
    original_fuel_level_analog: parseFloat(sample.original_fuel_level_analog),
    _original: sample
  }));
  
  return processed;
}

/**
 * Apply fuel smoothing for analysis
 * Even though device is filtered, apply robust smoothing to reduce spikes
 */
function smoothFuel(data, config) {
  const fuelLevels = data.map(d => d.fuelLevelLiters);
  
  // Step 1: Rolling median
  const fuelMed = rollingMedian(fuelLevels, config.smoothing_window);
  
  // Step 2: EWMA
  const fuelSmooth = ewma(fuelMed, config.ewma_alpha);
  
  // Attach to data
  return data.map((d, i) => ({
    ...d,
    fuel_smooth: fuelSmooth[i]
  }));
}

/**
 * Compute accelerometer-derived signals (gravity, tilt, vibration)
 */
function computeAccelSignals(data, config) {
  const result = [];
  const gravityWindow = config.gravity_window;
  const vibeWindow = config.vibe_window;
  const tiltDeltaSamples = Math.ceil(config.tilt_delta_seconds / 10); // Assuming ~10s sampling
  
  for (let i = 0; i < data.length; i++) {
    const point = { ...data[i] };
    
    // Compute gravity estimate (low-pass median over window)
    const start = Math.max(0, i - gravityWindow);
    const end = i + 1;
    const windowData = data.slice(start, end);
    
    const gx = median(windowData.map(d => d.x_axis));
    const gy = median(windowData.map(d => d.y_axis));
    const gz = median(windowData.map(d => d.z_axis));
    
    const gravity = [gx, gy, gz];
    const gravityUnit = normalize(gravity);
    
    point.gravity = gravity;
    point.gravity_unit = gravityUnit;
    
    // Compute tilt change score
    if (i >= tiltDeltaSamples) {
      const prevGravityUnit = result[i - tiltDeltaSamples].gravity_unit;
      const dotProduct = dot(gravityUnit, prevGravityUnit);
      // Clamp to [-1, 1] for numerical stability
      const clampedDot = Math.max(-1, Math.min(1, dotProduct));
      point.tilt_score = 1 - clampedDot;
      point.tilt_deg = Math.acos(clampedDot) * (180 / Math.PI);
    } else {
      point.tilt_score = 0;
      point.tilt_deg = 0;
    }
    
    // Compute vibration/activity score
    // Dynamic acceleration = accel - gravity
    const aDyn = [
      point.x_axis - gravity[0],
      point.y_axis - gravity[1],
      point.z_axis - gravity[2]
    ];
    
    // RMS of dynamic acceleration over window
    const vibeStart = Math.max(0, i - vibeWindow);
    const vibeWindowData = data.slice(vibeStart, i + 1);
    const dynMagnitudes = vibeWindowData.map(d => {
      const startIdx = Math.max(0, result.length - vibeWindow);
      const grav = startIdx < result.length && result[startIdx].gravity ? result[startIdx].gravity : [0, 0, 0];
      return magnitude([d.x_axis - grav[0], d.y_axis - grav[1], d.z_axis - grav[2]]);
    });
    
    // RMS
    const rms = Math.sqrt(dynMagnitudes.reduce((sum, v) => sum + v * v, 0) / dynMagnitudes.length);
    point.vibe = rms;
    
    result.push(point);
  }
  
  return result;
}

/**
 * Auto-calibrate vibration threshold
 */
function calibrateVibeThreshold(data, config) {
  const vibeValues = data.map(d => d.vibe).filter(v => v != null && !isNaN(v));
  
  if (config.vibe_threshold_strategy === 'p95') {
    return percentile(vibeValues, 95);
  } else if (config.vibe_threshold_strategy === 'p90') {
    return percentile(vibeValues, 90) * 1.1; // With margin
  } else if (typeof config.vibe_threshold_strategy === 'number') {
    return config.vibe_threshold_strategy;
  }
  
  // Default to p95
  return percentile(vibeValues, 95);
}

/**
 * Find candidate fuel loss events using baseline-to-baseline drops
 */
function findCandidateEvents(data, config, vibeThreshold) {
  const candidates = [];
  const preWindowMs = config.pre_window_minutes * 60 * 1000;
  const postWindowMs = config.post_window_minutes * 60 * 1000;
  const minSeparationMs = config.min_event_separation_minutes * 60 * 1000;
  
  // Scan for downward shifts
  for (let i = 0; i < data.length; i++) {
    const t = data[i].timestamp;
    
    // Get pre-window data
    const preData = data.filter(d => d.timestamp >= t - preWindowMs && d.timestamp < t);
    // Get post-window data
    const postData = data.filter(d => d.timestamp >= t && d.timestamp <= t + postWindowMs);
    
    if (preData.length < 3 || postData.length < 3) continue;
    
    const preLevel = median(preData.map(d => d.fuel_smooth));
    const postLevel = median(postData.map(d => d.fuel_smooth));
    const loss = preLevel - postLevel;
    
    // Check if loss meets minimum threshold
    if (loss < config.D_min_liters) continue;
    
    // Check post-window stability
    const postStddev = stddev(postData.map(d => d.fuel_smooth));
    if (postStddev > config.sigma_max) continue;
    
    // Check if we should merge with existing candidate
    const lastCandidate = candidates[candidates.length - 1];
    if (lastCandidate && t - lastCandidate.end_time < minSeparationMs) {
      // Merge: extend the event and update loss
      lastCandidate.end_time = t + postWindowMs;
      lastCandidate.post_level = Math.min(lastCandidate.post_level, postLevel);
      lastCandidate.loss_liters = lastCandidate.pre_level - lastCandidate.post_level;
      lastCandidate.candidate_index_end = i;
    } else {
      // New candidate
      candidates.push({
        candidate_id: candidates.length + 1,
        start_time: t - preWindowMs / 2,
        end_time: t + postWindowMs,
        t_detect: t,
        pre_level: preLevel,
        post_level: postLevel,
        loss_liters: loss,
        candidate_index_start: i,
        candidate_index_end: i,
        // Calculate initial confidence
        base_confidence: 0.5,
        // Check tilt near drop
        tilt_near_drop: data.slice(Math.max(0, i - 10), Math.min(data.length, i + 10))
          .some(d => d.tilt_deg >= config.T_tilt_deg),
        // Check if drop during low vibe
        low_vibe_at_drop: data[i].vibe < vibeThreshold,
        // Location at drop
        lat_long: data[i].lat_long || null
      });
    }
    
    // Skip forward to avoid redundant candidates
    i += Math.floor(postData.length / 2);
  }
  
  return candidates;
}

/**
 * Detect idle-to-active context for each candidate event.
 * Determines if the fuel drop coincides with activity resumption after a long parking/idle period.
 * Uses calculated_ignition (most accurate), calculated_idle, and speed_gnss.
 *
 * Key insight: the fuel sensor is filtered, so the detected drop time (t_detect) is DELAYED
 * from the actual physical event. The equipment may have started up minutes before t_detect.
 * We must skip past the recent "startup" active samples to find the actual parking period.
 */
function detectIdleContext(data, candidates, config) {
  const activityWindowMs = config.settling_activity_window_minutes * 60 * 1000;
  
  // Helper: is a sample "inactive" (parked/idle)?
  const isInactive = (d) => {
    // Engine off = definitely inactive
    if (d.calculated_ignition === 0) return true;
    // Engine on but idle = also inactive (not doing useful work)
    if (d.calculated_ignition === 1 && d.calculated_idle === 1) return true;
    return false;
  };
  
  for (const candidate of candidates) {
    const tDetect = candidate.t_detect;
    
    // Get all data before t_detect, sorted most-recent-first for backward scan
    const beforeData = data.filter(d => d.timestamp < tDetect);
    // beforeData is already sorted by timestamp ascending, so reverse for backward scan
    const reversed = [...beforeData].reverse();
    
    // --- Phase 1: Skip recent "startup" active samples ---
    // The equipment may have just started up before the filtered fuel drop appeared.
    // Skip any active samples at the end (most recent) to find where the parking period ends.
    let idlePhaseStartIdx = -1;
    let startupEndTimestamp = tDetect; // when the inactive period ended (= startup began)
    
    for (let i = 0; i < reversed.length; i++) {
      if (isInactive(reversed[i])) {
        idlePhaseStartIdx = i;
        if (i > 0) {
          // The previous sample (closer to t_detect) was the first active sample = startup time
          startupEndTimestamp = reversed[i - 1].timestamp;
        }
        break;
      }
    }
    
    if (idlePhaseStartIdx === -1) {
      // No inactive samples found before the drop — equipment was active the whole time
      candidate.idle_duration_before_drop = 0;
      candidate.activity_resumed_at_drop = false;
      continue;
    }
    
    // --- Phase 2: Measure the length of the inactive/parked period ---
    // Continue backward from where we found the first inactive sample, 
    // counting how long the inactive stretch lasts
    const idleEnd = reversed[idlePhaseStartIdx].timestamp; // most recent inactive sample
    let idleStart = idleEnd;
    
    for (let i = idlePhaseStartIdx; i < reversed.length; i++) {
      if (isInactive(reversed[i])) {
        idleStart = reversed[i].timestamp; // keep extending the idle period backward
      } else {
        break; // hit an active sample = this is where the equipment was last working
      }
    }
    
    const idleDurationMinutes = (idleEnd - idleStart) / (60 * 1000);
    candidate.idle_duration_before_drop = idleDurationMinutes;
    
    // --- Phase 3: Detect activity_resumed_at_drop ---
    // Check if there was a transition from inactive to active within the settling window
    // around t_detect. We use a generous window to account for filter lag.
    // The startup we detected in Phase 1 counts as activity resumption if it happened
    // close enough to t_detect.
    const startupDuration = tDetect - startupEndTimestamp; // ms between startup and detected drop
    
    // Also scan a wider window for explicit transitions
    const windowStart = tDetect - activityWindowMs;
    const windowEnd = tDetect + activityWindowMs;
    const windowData = data.filter(d => d.timestamp >= windowStart && d.timestamp <= windowEnd);
    
    let activityResumed = false;
    
    // Method 1: The startup we already found is within the settling window
    if (startupDuration <= activityWindowMs && startupDuration >= 0 && idleDurationMinutes > 0) {
      activityResumed = true;
    }
    
    // Method 2: Explicit transitions in the window (catches cases Method 1 misses)
    if (!activityResumed) {
      for (let i = 1; i < windowData.length; i++) {
        const prev = windowData[i - 1];
        const curr = windowData[i];
        
        // Ignition transition: 0 -> 1
        if (prev.calculated_ignition === 0 && curr.calculated_ignition === 1) {
          activityResumed = true;
          break;
        }
        
        // Speed transition: 0 -> moving
        if (prev.speed_gnss === 0 && curr.speed_gnss > 0) {
          activityResumed = true;
          break;
        }
        
        // Idle to active: idle=1 -> idle=0 (with ignition on)
        if (prev.calculated_idle === 1 && curr.calculated_idle === 0 && curr.calculated_ignition === 1) {
          activityResumed = true;
          break;
        }
      }
    }
    
    candidate.activity_resumed_at_drop = activityResumed;
    
    console.log(`[Idle Context] Event #${candidate.candidate_id}: idle_duration=${idleDurationMinutes.toFixed(1)}min, activity_resumed=${activityResumed}, startup_gap=${(startupDuration/60000).toFixed(1)}min, tilt_near_drop=${candidate.tilt_near_drop}`);
  }
  
  return candidates;
}

/**
 * Detect pre-drop fuel spike (slosh pattern where tilt inflates sensor reading before drop).
 * 
 * For each candidate, looks further back in time to find a stable baseline level,
 * then checks if the pre_level was artificially inflated by a spike.
 * If the post-drop level is close to that stable baseline, the "drop" was just
 * the sensor returning to its true reading after a tilt-induced spike.
 */
function detectPreDropSpike(data, candidates, config) {
  const preSpikeWindowMs = config.pre_spike_window_minutes * 60 * 1000;
  const preWindowMs = config.pre_window_minutes * 60 * 1000;

  for (const candidate of candidates) {
    const tDetect = candidate.t_detect;

    // Baseline window: [t_detect - pre_spike_window, t_detect - pre_window]
    // This is the period BEFORE the pre-window — should represent the true fuel level
    // before any tilt-induced spike inflated the reading
    const baselineStart = tDetect - preSpikeWindowMs;
    const baselineEnd = tDetect - preWindowMs;

    const baselineData = data.filter(d =>
      d.timestamp >= baselineStart && d.timestamp <= baselineEnd
    );

    if (baselineData.length < 3) {
      candidate.stable_baseline = null;
      candidate.has_pre_spike = false;
      candidate.pre_spike_amount = null;
      candidate.post_vs_baseline_diff = null;
      candidate.spike_loss_ratio = null;
      candidate.spike_is_symmetric = false;
      continue;
    }

    // Use LOCAL MINIMUM instead of median — correctly identifies the trough
    // before a slosh-inflated rise (median averages out the oscillation)
    const stableBaseline = Math.min(...baselineData.map(d => d.fuel_smooth));

    // How much higher is the pre_level compared to the stable baseline?
    const preSpikeAmount = candidate.pre_level - stableBaseline;

    // How close is the post-drop level to the stable baseline?
    const postVsBaselineDiff = Math.abs(candidate.post_level - stableBaseline);

    // Symmetry check: is the spike in the same ballpark as the loss?
    // If spike >> loss, the spike was likely a refuel event, not slosh
    const spikeLossRatio = candidate.loss_liters > 0 ? preSpikeAmount / candidate.loss_liters : 0;
    const spikeIsSymmetric = spikeLossRatio > 0 && spikeLossRatio <= config.spike_symmetry_max_ratio;

    candidate.stable_baseline = Math.round(stableBaseline * 100) / 100;
    candidate.has_pre_spike = preSpikeAmount >= config.pre_spike_threshold_liters;
    candidate.pre_spike_amount = Math.round(preSpikeAmount * 100) / 100;
    candidate.post_vs_baseline_diff = Math.round(postVsBaselineDiff * 100) / 100;
    candidate.spike_loss_ratio = Math.round(spikeLossRatio * 100) / 100;
    candidate.spike_is_symmetric = spikeIsSymmetric;

    if (candidate.has_pre_spike) {
      console.log(
        `[Pre-Spike] Event #${candidate.candidate_id}: ` +
        `stable_baseline=${stableBaseline.toFixed(2)}L, ` +
        `pre_level=${candidate.pre_level.toFixed(2)}L, ` +
        `spike=+${preSpikeAmount.toFixed(2)}L, ` +
        `post_level=${candidate.post_level.toFixed(2)}L, ` +
        `post_vs_baseline_diff=${postVsBaselineDiff.toFixed(2)}L, ` +
        `spike_loss_ratio=${spikeLossRatio.toFixed(2)}, ` +
        `symmetric=${spikeIsSymmetric}, ` +
        `tilt_near_drop=${candidate.tilt_near_drop}`
      );
    }
  }

  return candidates;
}

/**
 * Detect pre-drop context: fuel volatility, equipment operational state, and refuel proximity.
 * All windows are shifted back by filter_lag_minutes to account for Level 8 reporting delay.
 * 
 * Equipment states (from custom-calibrated fields):
 * - "working": calculated_ignition=1, calculated_idle=0 (actively operating, theft very unlikely)
 * - "idle": calculated_ignition=1, calculated_idle=1 (engine on but idle, theft possible)
 * - "off": calculated_ignition=0 (engine off, theft possible)
 */
function detectPreDropContext(data, candidates, config) {
  const volWindowMs = config.pre_drop_volatility_window_minutes * 60 * 1000;
  const opWindowMs = config.operational_check_window_minutes * 60 * 1000;
  const filterLagMs = config.filter_lag_minutes * 60 * 1000;
  const refuelProximityMs = config.refuel_proximity_minutes * 60 * 1000;

  for (const candidate of candidates) {
    const tDetect = candidate.t_detect;

    // --- Pre-drop volatility (shifted back by filter lag) ---
    const volStart = tDetect - volWindowMs - filterLagMs;
    const volEnd = tDetect - filterLagMs;
    const volData = data.filter(d => d.timestamp >= volStart && d.timestamp <= volEnd);

    if (volData.length >= 3) {
      // Use smoothed fuel data — raw data includes gradual trends that inflate stddev falsely
      candidate.pre_drop_volatility = Math.round(stddev(volData.map(d => d.fuel_smooth)) * 100) / 100;
    } else {
      candidate.pre_drop_volatility = null;
    }

    // --- Equipment operational state (shifted back by filter lag) ---
    const opStart = tDetect - opWindowMs - filterLagMs;
    const opEnd = tDetect - filterLagMs;
    const opData = data.filter(d => d.timestamp >= opStart && d.timestamp <= opEnd);

    let workingCount = 0;
    let idleCount = 0;
    let offCount = 0;

    for (const d of opData) {
      if (d.calculated_ignition === 1 && d.calculated_idle === 0) {
        workingCount++;
      } else if (d.calculated_ignition === 1 && d.calculated_idle === 1) {
        idleCount++;
      } else {
        offCount++;
      }
    }

    const totalOp = opData.length || 1;
    const workingFraction = workingCount / totalOp;

    // Determine dominant state by majority
    if (workingCount >= idleCount && workingCount >= offCount) {
      candidate.equipment_state = 'working';
    } else if (idleCount >= workingCount && idleCount >= offCount) {
      candidate.equipment_state = 'idle';
    } else {
      candidate.equipment_state = 'off';
    }

    candidate.working_fraction = Math.round(workingFraction * 100) / 100;
    candidate.actively_working_near_drop = workingFraction >= config.working_fraction_threshold;

    // --- Near-drop idle check (short window, NO filter lag shift) ---
    // Ignition/idle state is reported in near-real-time, so we check
    // the 3 minutes right before the drop to catch recent transitions to idle/off.
    // This prevents stale "working" state from dismissing drops that occur after
    // the equipment has already stopped working.
    const nearDropWindowMs = 3 * 60 * 1000; // 3 minutes
    const nearDropData = data.filter(d =>
      d.timestamp >= (tDetect - nearDropWindowMs) && d.timestamp <= tDetect
    );
    const idleOrOffCount = nearDropData.filter(d =>
      d.calculated_idle === 1 || d.calculated_ignition === 0
    ).length;
    candidate.idle_near_drop = nearDropData.length > 0 &&
      (idleOrOffCount / nearDropData.length) >= 0.3;

    // --- Near refuel check (using flag_to_check from raw data) ---
    const refuelStart = tDetect - refuelProximityMs;
    const refuelEnd = tDetect + refuelProximityMs;
    const nearbyData = data.filter(d => d.timestamp >= refuelStart && d.timestamp <= refuelEnd);

    candidate.near_refuel = nearbyData.some(d =>
      d.flag_to_check && d.flag_to_check.startsWith('Possible Refueling')
    );

    console.log(
      `[PreDropContext] Event #${candidate.candidate_id}: ` +
      `volatility=${candidate.pre_drop_volatility}L, ` +
      `state=${candidate.equipment_state}, ` +
      `working_frac=${candidate.working_fraction}, ` +
      `actively_working=${candidate.actively_working_near_drop}, ` +
      `idle_near_drop=${candidate.idle_near_drop}, ` +
      `near_refuel=${candidate.near_refuel}`
    );
  }

  return candidates;
}

function summarizeStateWindow(windowData) {
  if (!windowData || windowData.length === 0) {
    return {
      dominantState: null,
      workingFraction: 0,
      idleFraction: 0,
      offFraction: 0
    };
  }

  let working = 0;
  let idle = 0;
  let off = 0;

  for (const d of windowData) {
    if (d.calculated_ignition === 1 && d.calculated_idle === 0) {
      working++;
    } else if (d.calculated_ignition === 1 && d.calculated_idle === 1) {
      idle++;
    } else {
      off++;
    }
  }

  const total = windowData.length;
  const workingFraction = working / total;
  const idleFraction = idle / total;
  const offFraction = off / total;

  let dominantState = 'off';
  if (working >= idle && working >= off) dominantState = 'working';
  else if (idle >= working && idle >= off) dominantState = 'idle';

  return { dominantState, workingFraction, idleFraction, offFraction };
}

function detectStatePriorityContext(data, candidates, config) {
  const filterLagMs = config.filter_lag_minutes * 60 * 1000;
  const lagWindowMs = config.state_lag_window_minutes * 60 * 1000;
  const recentWindowMs = config.state_recent_window_minutes * 60 * 1000;

  for (const candidate of candidates) {
    const tDetect = candidate.t_detect;
    const lagWindowEnd = tDetect - filterLagMs;
    const lagWindowStart = lagWindowEnd - lagWindowMs;
    const recentWindowStart = tDetect - recentWindowMs;

    const lagWindowData = data.filter(d => d.timestamp >= lagWindowStart && d.timestamp <= lagWindowEnd);
    const recentWindowData = data.filter(d => d.timestamp >= recentWindowStart && d.timestamp <= tDetect);

    const lagSummary = summarizeStateWindow(lagWindowData);
    const recentSummary = summarizeStateWindow(recentWindowData);

    candidate.state_lag_window = lagSummary;
    candidate.state_recent_window = recentSummary;

    const lagTheftPossible = lagSummary.dominantState === 'off' || lagSummary.dominantState === 'idle';
    const recentTheftPossible = recentSummary.dominantState === 'off' || recentSummary.dominantState === 'idle';
    const lagWorking = lagSummary.dominantState === 'working';
    const recentWorking = recentSummary.dominantState === 'working';

    candidate.theft_possible_context = lagTheftPossible || recentTheftPossible;
    candidate.working_context = lagWorking && recentWorking;
    candidate.state_conflict = candidate.theft_possible_context && candidate.working_context;
  }

  return candidates;
}

function detectSensorErrorContext(data, candidates, config) {
  const sensorWindowMs = config.sensor_error_window_minutes * 60 * 1000;
  const strongDurationMs = config.sensor_error_strong_duration_minutes * 60 * 1000;

  const isSensorError = (d) =>
    d.original_fuel_level_analog != null &&
    !isNaN(d.original_fuel_level_analog) &&
    (d.original_fuel_level_analog === 0 || d.original_fuel_level_analog >= 6500);

  for (const candidate of candidates) {
    const tDetect = candidate.t_detect;
    const windowStart = tDetect - sensorWindowMs;
    const windowEnd = candidate.end_time + sensorWindowMs;
    const aroundData = data.filter(d => d.timestamp >= windowStart && d.timestamp <= windowEnd);
    const errorPackets = aroundData.filter(isSensorError);

    candidate.sensor_error_packets_near_drop = errorPackets.length;
    candidate.sensor_error_near_drop = errorPackets.length > 0;

    let continuousErrorDurationMs = 0;
    let currentStart = null;
    let previousTs = null;
    const maxGapMs = 60 * 1000;

    for (const d of aroundData) {
      if (!isSensorError(d)) {
        currentStart = null;
        previousTs = null;
        continue;
      }

      if (currentStart == null) {
        currentStart = d.timestamp;
        previousTs = d.timestamp;
      } else {
        if (d.timestamp - previousTs > maxGapMs) {
          currentStart = d.timestamp;
        }
        previousTs = d.timestamp;
      }

      const runDuration = d.timestamp - currentStart;
      if (runDuration > continuousErrorDurationMs) {
        continuousErrorDurationMs = runDuration;
      }
    }

    candidate.sensor_error_continuous_duration_minutes = continuousErrorDurationMs / (60 * 1000);
    candidate.sensor_error_strong =
      candidate.sensor_error_packets_near_drop >= config.sensor_error_strong_min_samples &&
      continuousErrorDurationMs >= strongDurationMs;
  }

  return candidates;
}

/**
 * Detect recovery opportunities after candidate events
 */
function detectRecoveryOpportunities(data, candidates, config, vibeThreshold) {
  const tiltThreshold = config.T_tilt_deg;
  const activeSustainMs = config.active_sustain_seconds * 1000;
  const speedThreshold = config.speed_threshold_kmh;
  
  for (const candidate of candidates) {
    const candidateEnd = candidate.end_time;
    
    // Find data after candidate
    const afterData = data.filter(d => d.timestamp > candidateEnd);
    
    if (afterData.length === 0) {
      candidate.t_opportunity = null;
      candidate.opportunity_type = null;
      continue;
    }
    
    let opportunity = null;
    
    // Check for tilt change
    for (const d of afterData) {
      if (d.tilt_deg >= tiltThreshold) {
        opportunity = {
          timestamp: d.timestamp,
          type: 'tilt',
          tilt_deg: d.tilt_deg,
          vibe: d.vibe
        };
        break;
      }
    }
    
    // Check for activity onset (sustained vibration)
    if (!opportunity) {
      for (let i = 0; i < afterData.length; i++) {
        if (afterData[i].vibe > vibeThreshold) {
          // Check if sustained
          let sustained = true;
          const sustainSamples = Math.ceil(activeSustainMs / 10000); // ~10s per sample
          for (let j = 0; j < sustainSamples && i + j < afterData.length; j++) {
            if (afterData[i + j].vibe <= vibeThreshold) {
              sustained = false;
              break;
            }
          }
          if (sustained) {
            opportunity = {
              timestamp: afterData[i].timestamp,
              type: 'activity',
              tilt_deg: afterData[i].tilt_deg,
              vibe: afterData[i].vibe
            };
            break;
          }
        }
      }
    }
    
    // Check for movement (if speed available)
    if (!opportunity) {
      for (let i = 0; i < afterData.length; i++) {
        if (afterData[i].speed_gnss >= speedThreshold) {
          // Check if sustained
          let sustained = true;
          const sustainSamples = Math.ceil(activeSustainMs / 10000);
          for (let j = 0; j < sustainSamples && i + j < afterData.length; j++) {
            if (afterData[i + j].speed_gnss < speedThreshold) {
              sustained = false;
              break;
            }
          }
          if (sustained) {
            opportunity = {
              timestamp: afterData[i].timestamp,
              type: 'movement',
              tilt_deg: afterData[i].tilt_deg,
              vibe: afterData[i].vibe
            };
            break;
          }
        }
      }
    }
    
    if (opportunity) {
      candidate.t_opportunity = opportunity.timestamp;
      candidate.opportunity_type = opportunity.type;
      candidate.tilt_deg_at_opportunity = opportunity.tilt_deg;
      candidate.vibe_at_opportunity = opportunity.vibe;
    } else {
      candidate.t_opportunity = null;
      candidate.opportunity_type = null;
    }
  }
  
  return candidates;
}

/**
 * Evaluate recovery after opportunity and classify events
 */
function classifyEvents(data, candidates, config) {
  const filterLagMs = config.filter_lag_minutes * 60 * 1000;
  const recoveryWindowMs = config.recovery_window_minutes * 60 * 1000;
  
  for (const candidate of candidates) {
    candidate.reason_tags = [];

    if (!candidate.t_opportunity) {
      // No recovery opportunity found
      candidate.status = EVENT_STATUS.POSSIBLE_UNCONFIRMED;
      candidate.recovery_ratio = null;
      candidate.filter_lag_used_minutes = config.filter_lag_minutes;
      candidate.reason_tags.push('No recovery opportunity detected');

      if (candidate.sensor_error_strong) {
        candidate.status = EVENT_STATUS.DISMISSED_SENSOR_ERROR;
        candidate.reason_tags.push('Strong sensor error sequence near drop');
      } else if (candidate.sensor_error_near_drop) {
        candidate.status = EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED;
        candidate.reason_tags.push('Sensor error packets near drop');
      }
      continue;
    }
    
    // Evaluate fuel recovery after opportunity (with lag)
    const tStart = candidate.t_opportunity + filterLagMs;
    const tEnd = tStart + recoveryWindowMs;
    
    // Get fuel data in recovery window
    const recoveryData = data.filter(d => d.timestamp >= tStart && d.timestamp <= tEnd);
    
    if (recoveryData.length === 0) {
      candidate.status = EVENT_STATUS.POSSIBLE_UNCONFIRMED;
      candidate.recovery_ratio = null;
      candidate.filter_lag_used_minutes = config.filter_lag_minutes;
      continue;
    }
    
    // Get local minimum around candidate post window
    const postWindowData = data.filter(d => 
      d.timestamp >= candidate.t_detect && 
      d.timestamp <= candidate.end_time
    );
    const low = Math.min(...postWindowData.map(d => d.fuel_smooth));
    
    // Get max after opportunity
    const maxAfter = Math.max(...recoveryData.map(d => d.fuel_smooth));
    
    const recovery = maxAfter - low;
    const recoveryRatio = candidate.loss_liters > 0 ? recovery / candidate.loss_liters : 0;
    
    candidate.recovery_ratio = recoveryRatio;
    candidate.recovery_amount = recovery;
    candidate.low_level = low;
    candidate.max_after_opportunity = maxAfter;
    candidate.filter_lag_used_minutes = config.filter_lag_minutes;

    if (candidate.theft_possible_context) {
      candidate.reason_tags.push('Lag-aware state indicates off/idle near drop');
    }
    if (candidate.working_context) {
      candidate.reason_tags.push('Lag-aware state indicates active working near drop');
    }
    if (candidate.sensor_error_near_drop) {
      candidate.reason_tags.push('Sensor error packets near drop');
    }
    
    // Classification
    if (candidate.sensor_error_strong) {
      candidate.status = EVENT_STATUS.DISMISSED_SENSOR_ERROR;
      candidate.reason_tags.push('Dismissed as sensor artifact');
      continue;
    }

    // First check: dismiss as settling if drop coincides with activity resumption after long parking
    if (candidate.activity_resumed_at_drop &&
        candidate.idle_duration_before_drop >= config.min_idle_duration_for_settling_minutes &&
        candidate.tilt_near_drop) {
      candidate.status = EVENT_STATUS.DISMISSED_SETTLING;
      candidate.reason_tags.push('Settling pattern after long idle');
      continue;
    }
    
    // Second check: dismiss as spike-return artifact if fuel spiked before drop and post-level
    // matches the stable baseline (sensor returned to true level after inflated reading).
    // Symmetry check protects against refuel-like spikes.
    // Do not require tilt flag here; tilt signal can be missed on some packets.
    if (candidate.has_pre_spike &&
        candidate.spike_is_symmetric &&
        candidate.post_vs_baseline_diff <= config.post_vs_baseline_tolerance_liters) {
      candidate.status = EVENT_STATUS.DISMISSED_TILT_SLOSH;
      candidate.reason_tags.push(
        candidate.theft_possible_context
          ? 'Pre-spike symmetric return-to-baseline pattern (dismissed despite off/idle context to reduce false possible leakage)'
          : 'Pre-spike symmetric return-to-baseline pattern'
      );
      continue;
    }
    
    // Third check: dismiss as sloshing if tilt present AND either:
    //   Path A: high fuel volatility (oscillating readings = sloshing)
    //   Path B: equipment actively working AND not idle near the drop
    //           (if equipment went idle before the drop, theft is possible even if it was working earlier)
    if (candidate.tilt_near_drop && (
        (candidate.pre_drop_volatility != null &&
         candidate.pre_drop_volatility >= config.pre_drop_volatility_threshold) ||
        (candidate.actively_working_near_drop && !candidate.idle_near_drop)
    )) {
      if (candidate.theft_possible_context) {
        candidate.status = EVENT_STATUS.POSSIBLE_LEAKAGE;
        candidate.reason_tags.push('Volatile/tilt pattern but off/idle context keeps event as possible leakage');
      } else {
        candidate.status = EVENT_STATUS.DISMISSED_TILT_SLOSH;
        candidate.reason_tags.push('Dismissed due to high tilt/volatility working-context pattern');
      }
      continue;
    }
    
    // Recovery-based classification with symmetry guard:
    // If recovery >> loss (ratio > max), the "recovery" is likely a refuel, not slosh → don't dismiss
    const recoveryIsSymmetric = recoveryRatio <= config.recovery_symmetry_max_ratio;
    
    if (recoveryRatio >= config.recovery_ratio_dismiss && recoveryIsSymmetric) {
      candidate.status = EVENT_STATUS.DISMISSED_TILT_SLOSH;
      candidate.reason_tags.push(
        candidate.theft_possible_context
          ? 'High symmetric recovery indicates non-persistent drop (dismissed despite off/idle context)'
          : 'High symmetric recovery indicates slosh/reorientation'
      );
    } else if (recoveryRatio <= config.recovery_ratio_confirm) {
      // Additional persistence check
      const lastWindowData = recoveryData.slice(-Math.ceil(recoveryData.length / 2));
      const medianLastWindow = median(lastWindowData.map(d => d.fuel_smooth));
      
      if (medianLastWindow <= candidate.pre_level - 0.8 * candidate.loss_liters) {
        if (candidate.working_context && !candidate.theft_possible_context) {
          candidate.status = EVENT_STATUS.POSSIBLE_LEAKAGE;
          candidate.reason_tags.push('Persistent loss but working-context prevents confirmation');
        } else {
          candidate.status = EVENT_STATUS.CONFIRMED_LEAKAGE;
          candidate.reason_tags.push('Persistent low level after drop');
        }
      } else {
        candidate.status = EVENT_STATUS.POSSIBLE_LEAKAGE;
        candidate.reason_tags.push('Low recovery but persistence check not strong enough');
      }
    } else {
      candidate.status = EVENT_STATUS.POSSIBLE_LEAKAGE;
      candidate.reason_tags.push('Intermediate recovery band');
    }

    if (candidate.sensor_error_near_drop && candidate.status !== EVENT_STATUS.DISMISSED_SENSOR_ERROR) {
      if (
        candidate.status === EVENT_STATUS.CONFIRMED_LEAKAGE ||
        candidate.status === EVENT_STATUS.POSSIBLE_LEAKAGE ||
        candidate.status === EVENT_STATUS.POSSIBLE_UNCONFIRMED
      ) {
        candidate.status = EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED;
      }
      candidate.reason_tags.push('Sensor error influence retained as caution');
    }
  }
  
  return candidates;
}

/**
 * Calculate confidence scores for events
 */
function calculateConfidence(candidates, config) {
  for (const candidate of candidates) {
    let confidence = 0.5; // Base confidence
    
    // +0.3 if no tilt change near drop
    if (!candidate.tilt_near_drop) {
      confidence += 0.3;
    }
    
    // +0.2 if drop occurs during low vibe (quiet)
    if (candidate.low_vibe_at_drop) {
      confidence += 0.2;
    }
    
    // -0.4 if high recovery ratio
    if (candidate.recovery_ratio != null && candidate.recovery_ratio >= config.recovery_ratio_dismiss) {
      confidence -= 0.4;
    }
    
    // +0.3 if persists across opportunity
    if (candidate.status === EVENT_STATUS.CONFIRMED_LEAKAGE) {
      confidence += 0.3;
    } else if (
      (candidate.status === EVENT_STATUS.POSSIBLE_LEAKAGE ||
       candidate.status === EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED) &&
      candidate.recovery_ratio != null
    ) {
      confidence += 0.15;
    }
    
    // -0.3 if drop coincides with activity resumption after idle (settling indicator)
    if (candidate.activity_resumed_at_drop && candidate.idle_duration_before_drop >= 30) {
      confidence -= 0.3;
    }
    
    // -0.3 if pre-drop spike detected with tilt (slosh indicator even if not fully dismissed)
    if (candidate.has_pre_spike && candidate.tilt_near_drop) {
      confidence -= 0.3;
    }
    
    // -0.3 if high pre-drop volatility + tilt (volatile fuel + tilt = strong sloshing indicator)
    if (candidate.pre_drop_volatility != null &&
        candidate.pre_drop_volatility >= config.pre_drop_volatility_threshold &&
        candidate.tilt_near_drop) {
      confidence -= 0.3;
    }
    
    // -0.2 if equipment was actively working (theft very unlikely during active operation)
    if (candidate.actively_working_near_drop) {
      confidence -= 0.2;
    }
    
    // +0.2 if equipment was off + fuel was stable (classic theft scenario)
    if (candidate.equipment_state === 'off' &&
        candidate.pre_drop_volatility != null &&
        candidate.pre_drop_volatility < config.pre_drop_volatility_threshold / 2) {
      confidence += 0.2;
    }
    
    // +0.1 if equipment was idling + fuel was stable (possible theft scenario)
    if (candidate.equipment_state === 'idle' &&
        candidate.pre_drop_volatility != null &&
        candidate.pre_drop_volatility < config.pre_drop_volatility_threshold / 2) {
      confidence += 0.1;
    }
    
    // +0.1 if near a refuel event (theft near refueling is a known pattern)
    if (candidate.near_refuel) {
      confidence += 0.1;
    }

    if (candidate.sensor_error_near_drop) {
      confidence -= 0.15;
    }
    
    // Dismissed settling events get very low confidence
    if (candidate.status === EVENT_STATUS.DISMISSED_SETTLING) {
      confidence = 0.1;
    }
    
    // Dismissed tilt/slosh events get very low confidence
    if (candidate.status === EVENT_STATUS.DISMISSED_TILT_SLOSH) {
      confidence = 0.1;
    }

    // Dismissed sensor artifact events get very low confidence
    if (candidate.status === EVENT_STATUS.DISMISSED_SENSOR_ERROR) {
      confidence = 0.05;
    }
    
    // Clamp to [0, 1]
    candidate.confidence = Math.max(0, Math.min(1, confidence));
  }
  
  return candidates;
}

/**
 * Format events for output
 */
function formatEvents(candidates, assetId) {
  return candidates.map(candidate => ({
    asset_id: assetId,
    event_id: candidate.candidate_id,
    start_time_detected: candidate.start_time,
    start_time_formatted: new Date(candidate.start_time).toISOString(),
    end_time_detected: candidate.end_time,
    end_time_formatted: new Date(candidate.end_time).toISOString(),
    t_detect: candidate.t_detect,
    t_detect_formatted: new Date(candidate.t_detect).toISOString(),
    loss_liters: Math.round(candidate.loss_liters * 100) / 100,
    pre_level_liters: Math.round(candidate.pre_level * 100) / 100,
    post_level_liters: Math.round(candidate.post_level * 100) / 100,
    lat_long: candidate.lat_long || null,
    status: candidate.status,
    confidence: Math.round(candidate.confidence * 100) / 100,
    evidence: {
      t_opportunity: candidate.t_opportunity,
      t_opportunity_formatted: candidate.t_opportunity ? new Date(candidate.t_opportunity).toISOString() : null,
      opportunity_type: candidate.opportunity_type,
      tilt_deg_at_opportunity: candidate.tilt_deg_at_opportunity != null 
        ? Math.round(candidate.tilt_deg_at_opportunity * 100) / 100 
        : null,
      vibe_at_opportunity: candidate.vibe_at_opportunity != null
        ? Math.round(candidate.vibe_at_opportunity * 100) / 100
        : null,
      recovery_ratio: candidate.recovery_ratio != null 
        ? Math.round(candidate.recovery_ratio * 100) / 100 
        : null,
      recovery_amount: candidate.recovery_amount != null
        ? Math.round(candidate.recovery_amount * 100) / 100
        : null,
      filter_lag_used_minutes: candidate.filter_lag_used_minutes,
      tilt_near_drop: candidate.tilt_near_drop,
      low_vibe_at_drop: candidate.low_vibe_at_drop,
      idle_duration_before_drop: candidate.idle_duration_before_drop != null
        ? Math.round(candidate.idle_duration_before_drop)
        : null,
      activity_resumed_at_drop: candidate.activity_resumed_at_drop || false,
      has_pre_spike: candidate.has_pre_spike || false,
      pre_spike_amount: candidate.pre_spike_amount != null
        ? Math.round(candidate.pre_spike_amount * 100) / 100
        : null,
      stable_baseline: candidate.stable_baseline != null
        ? Math.round(candidate.stable_baseline * 100) / 100
        : null,
      post_vs_baseline_diff: candidate.post_vs_baseline_diff != null
        ? Math.round(candidate.post_vs_baseline_diff * 100) / 100
        : null,
      spike_loss_ratio: candidate.spike_loss_ratio != null
        ? Math.round(candidate.spike_loss_ratio * 100) / 100
        : null,
      spike_is_symmetric: candidate.spike_is_symmetric || false,
      pre_drop_volatility: candidate.pre_drop_volatility != null
        ? Math.round(candidate.pre_drop_volatility * 100) / 100
        : null,
      equipment_state: candidate.equipment_state || null,
      working_fraction: candidate.working_fraction != null
        ? Math.round(candidate.working_fraction * 100) / 100
        : null,
      actively_working_near_drop: candidate.actively_working_near_drop || false,
      idle_near_drop: candidate.idle_near_drop || false,
      near_refuel: candidate.near_refuel || false,
      theft_possible_context: candidate.theft_possible_context || false,
      working_context: candidate.working_context || false,
      state_lag_window: candidate.state_lag_window || null,
      state_recent_window: candidate.state_recent_window || null,
      sensor_error_near_drop: candidate.sensor_error_near_drop || false,
      sensor_error_packets_near_drop: candidate.sensor_error_packets_near_drop || 0,
      sensor_error_continuous_duration_minutes: candidate.sensor_error_continuous_duration_minutes != null
        ? Math.round(candidate.sensor_error_continuous_duration_minutes * 10) / 10
        : null,
      reason_tags: candidate.reason_tags || []
    }
  }));
}

/**
 * Generate debug series for charting
 */
function generateDebugSeries(data, events) {
  // Create markers for events
  const eventMarkers = events.map(event => ({
    timestamp: event.t_detect,
    timestamp_formatted: event.t_detect_formatted,
    status: event.status,
    loss_liters: event.loss_liters,
    confidence: event.confidence
  }));
  
  return {
    timestamps: data.map(d => d.timestamp),
    datetime_ist: data.map(d => d.datetime_ist),
    fuel_raw: data.map(d => d.fuelLevelLiters),
    fuel_smooth: data.map(d => d.fuel_smooth),
    tilt_deg: data.map(d => d.tilt_deg),
    vibe: data.map(d => d.vibe),
    speed: data.map(d => d.speed_gnss),
    ignition: data.map(d => d.calculated_ignition),
    idle: data.map(d => d.calculated_idle),
    gravity_x: data.map(d => d.gravity ? d.gravity[0] : null),
    gravity_y: data.map(d => d.gravity ? d.gravity[1] : null),
    gravity_z: data.map(d => d.gravity ? d.gravity[2] : null),
    event_markers: eventMarkers
  };
}

/**
 * Main detection function
 * 
 * @param {Array} samples - Raw telemetry data samples
 * @param {Object} config - Configuration options (uses DEFAULT_CONFIG if not provided)
 * @param {string} assetId - Asset identifier for output
 * @param {boolean} includeDebugSeries - Whether to include debug series in output
 * @returns {Object} Detection results with events and optional debug series
 */
export function detectFuelLeakageEvents(samples, config = {}, assetId = 'unknown', includeDebugSeries = false) {
  // Merge config with defaults
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  
  // Validate input
  if (!samples || !Array.isArray(samples) || samples.length === 0) {
    return {
      success: false,
      error: 'No samples provided',
      events: [],
      summary: { total_events: 0 }
    };
  }
  
  try {
    // Step 1: Preprocess data
    const preprocessed = preprocessData(samples, mergedConfig);
    
    if (preprocessed.length < 10) {
      return {
        success: false,
        error: 'Insufficient data points after preprocessing (need at least 10)',
        events: [],
        summary: { total_events: 0 }
      };
    }
    
    // Step 2: Smooth fuel data
    const smoothed = smoothFuel(preprocessed, mergedConfig);
    
    // Step 3: Compute accelerometer signals
    const withAccelSignals = computeAccelSignals(smoothed, mergedConfig);
    
    // Step 4: Calibrate vibration threshold
    const vibeThreshold = calibrateVibeThreshold(withAccelSignals, mergedConfig);
    
    // Step 5: Find candidate events
    let candidates = findCandidateEvents(withAccelSignals, mergedConfig, vibeThreshold);
    
    // Step 5b: Detect idle-to-active context for each candidate (parking/settling awareness)
    candidates = detectIdleContext(withAccelSignals, candidates, mergedConfig);
    
    // Step 5c: Detect pre-drop fuel spikes (tilt-inflated sensor readings before drop)
    candidates = detectPreDropSpike(withAccelSignals, candidates, mergedConfig);
    
    // Step 5d: Detect pre-drop context (volatility, operational state, refuel proximity)
    candidates = detectPreDropContext(withAccelSignals, candidates, mergedConfig);

    // Step 5e: Add lag-aware state-priority context for classification gating
    candidates = detectStatePriorityContext(withAccelSignals, candidates, mergedConfig);

    // Step 5f: Detect raw-sensor error influence near each candidate
    candidates = detectSensorErrorContext(withAccelSignals, candidates, mergedConfig);
    
    // Step 6: Detect recovery opportunities
    candidates = detectRecoveryOpportunities(withAccelSignals, candidates, mergedConfig, vibeThreshold);
    
    // Step 7: Classify events
    candidates = classifyEvents(withAccelSignals, candidates, mergedConfig);
    
    // Step 8: Calculate confidence
    candidates = calculateConfidence(candidates, mergedConfig);
    
    // Step 9: Format output
    const events = formatEvents(candidates, assetId);
    
    // Count by status
    const statusCounts = events.reduce((acc, e) => {
      acc[e.status] = (acc[e.status] || 0) + 1;
      return acc;
    }, {});
    
    const result = {
      success: true,
      events,
      summary: {
        total_events: events.length,
        confirmed_leakage: statusCounts[EVENT_STATUS.CONFIRMED_LEAKAGE] || 0,
        possible_leakage: (statusCounts[EVENT_STATUS.POSSIBLE_LEAKAGE] || 0) +
          (statusCounts[EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED] || 0),
        possible_leakage_sensor_affected: statusCounts[EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED] || 0,
        possible_unconfirmed: statusCounts[EVENT_STATUS.POSSIBLE_UNCONFIRMED] || 0,
        dismissed_tilt_slosh: statusCounts[EVENT_STATUS.DISMISSED_TILT_SLOSH] || 0,
        dismissed_settling: statusCounts[EVENT_STATUS.DISMISSED_SETTLING] || 0,
        dismissed_sensor_error: statusCounts[EVENT_STATUS.DISMISSED_SENSOR_ERROR] || 0,
        total_loss_liters: events
          .filter(e =>
            e.status !== EVENT_STATUS.DISMISSED_TILT_SLOSH &&
            e.status !== EVENT_STATUS.DISMISSED_SETTLING &&
            e.status !== EVENT_STATUS.DISMISSED_SENSOR_ERROR
          )
          .reduce((sum, e) => sum + e.loss_liters, 0),
        vibe_threshold_used: Math.round(vibeThreshold * 100) / 100,
        data_points_analyzed: withAccelSignals.length,
        time_range: {
          start: withAccelSignals[0].timestamp,
          start_formatted: new Date(withAccelSignals[0].timestamp).toISOString(),
          end: withAccelSignals[withAccelSignals.length - 1].timestamp,
          end_formatted: new Date(withAccelSignals[withAccelSignals.length - 1].timestamp).toISOString()
        }
      },
      config_used: mergedConfig
    };
    
    // Add debug series if requested
    if (includeDebugSeries) {
      result.debug_series = generateDebugSeries(withAccelSignals, events);
    }
    
    return result;
    
  } catch (error) {
    return {
      success: false,
      error: error.message,
      events: [],
      summary: { total_events: 0 }
    };
  }
}

/**
 * Validate that samples have required fields
 */
export function validateSamples(samples) {
  if (!samples || !Array.isArray(samples) || samples.length === 0) {
    return { valid: false, error: 'No samples provided' };
  }
  
  const required = ['fuelLevelLiters', 'x_axis', 'y_axis', 'z_axis'];
  const optional = ['speed_gnss', 'calculated_ignition', 'datetime_ist'];
  
  const sample = samples[0];
  const missing = required.filter(field => 
    sample[field] === undefined && sample[field.toLowerCase()] === undefined
  );
  
  if (missing.length > 0) {
    return { valid: false, error: `Missing required fields: ${missing.join(', ')}` };
  }
  
  // Check for timestamp
  if (!sample.timestamp && !sample.datetime_ist && !sample.datetime) {
    return { valid: false, error: 'Missing timestamp field (timestamp or datetime_ist)' };
  }
  
  const presentOptional = optional.filter(field => 
    sample[field] !== undefined || sample[field.toLowerCase()] !== undefined
  );
  
  return { 
    valid: true, 
    required_fields: required,
    optional_fields_present: presentOptional,
    sample_count: samples.length
  };
}
